import React, { useEffect, useMemo, useState } from "react";
import type { CourseModule, LessonItem, ProductItem } from "../services/api";
import type { CourseItem, SelectedLessonState } from "../appTypes";
import "./CoursePage.css";

interface LessonLink {
  courseId: string;
  courseTitle: string;
  moduleTitle: string;
  lesson: LessonItem;
}

interface CoursePageProps {
  accessibleCourses: ProductItem[];
  courseModules: Record<string, CourseModule[]>;
  selectedLesson: SelectedLessonState | null;
  selectedCourseModules: CourseModule[];
  selectedCourseLessonList: LessonLink[];
  selectedLessonIndex: number;
  previousLesson: LessonLink | null;
  nextLesson: LessonLink | null;
  watchedLessonsSet: Set<number>;
  mapModulesToCourses: (modules: CourseModule[]) => CourseItem[];
  handleSelectLesson: (courseId: string, courseTitle: string, lessonId: number) => void;
  getEmbedVideoUrl: (url: string) => string;
  loadingData: boolean;
  pageError: string;
  userName?: string;
  overallProgress?: number;
  onOpenCertificates?: () => void;
}

function materialKind(url: string) {
  const u = (url || "").toLowerCase();
  if (/\.(pdf)(\?|$)/.test(u)) return { label: "PDF", download: true };
  if (/\.(xlsx?|csv)(\?|$)/.test(u)) return { label: "Planilha", download: true };
  if (/\.(zip|rar|7z)(\?|$)/.test(u)) return { label: "Arquivo", download: true };
  if (/\.(docx?|pptx?)(\?|$)/.test(u)) return { label: "Documento", download: true };
  if (/\.(png|jpe?g|webp|gif)(\?|$)/.test(u)) return { label: "Imagem", download: true };
  return { label: "Link", download: false };
}

const FileIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M5 4h9l5 5v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" /><path d="M14 4v5h5" /></svg>);
const LinkIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" /></svg>);

const CoursePage: React.FC<CoursePageProps> = ({
  accessibleCourses,
  courseModules,
  selectedLesson,
  previousLesson,
  nextLesson,
  watchedLessonsSet,
  handleSelectLesson,
  getEmbedVideoUrl,
  loadingData,
  pageError,
  overallProgress = 0,
  onOpenCertificates,
}) => {
  const [view, setView] = useState<"overview" | "player">("overview");

  const currentCourse =
    accessibleCourses.find((c) => c.id === selectedLesson?.courseId) || accessibleCourses[0] || null;
  const modules = currentCourse ? courseModules[currentCourse.id] || [] : [];
  const allLessons = useMemo(() => modules.flatMap((m) => m.lessons), [modules]);
  const watchedCount = allLessons.filter((l) => watchedLessonsSet.has(l.id)).length;
  const coursePct = allLessons.length ? Math.round((watchedCount / allLessons.length) * 100) : 0;
  const externalUrl = !allLessons.length ? (currentCourse?.accessUrl || currentCourse?.productUrl || "") : "";
  const isExternalLive = /^https?:\/\//i.test(externalUrl);

  const currentModule = selectedLesson
    ? modules.find((m) => m.lessons.some((l) => l.id === selectedLesson.lesson.id)) || null
    : null;
  const moduleLessons = currentModule?.lessons || [];
  const lessonIdxInModule = selectedLesson
    ? moduleLessons.findIndex((l) => l.id === selectedLesson.lesson.id)
    : -1;

  // Ao trocar de curso, volta para o overview.
  useEffect(() => { setView("overview"); }, [currentCourse?.id]);

  function openModule(course: ProductItem, mod: CourseModule) {
    const first = mod.lessons[0];
    if (!first) return;
    handleSelectLesson(course.id, course.title, first.id);
    setView("player");
  }

  function continueCourse() {
    if (!currentCourse) return;
    const next = allLessons.find((l) => !watchedLessonsSet.has(l.id)) || allLessons[0];
    if (next) { handleSelectLesson(currentCourse.id, currentCourse.title, next.id); setView("player"); }
  }

  if (loadingData) return <div className="bc-page"><div className="bc-empty">Carregando o curso…</div></div>;
  if (pageError) return <div className="bc-page"><div className="bc-empty">{pageError}</div></div>;
  if (!accessibleCourses.length || !currentCourse) {
    return (
      <div className="bc-page">
        <div className="bc-locked">
          <span className="bc-locked-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M6.5 5.25h9.75A1.75 1.75 0 0 1 18 7v11.25H8.25A2.75 2.75 0 0 0 5.5 21V6.25a1 1 0 0 1 1-1Z" /><path d="M8 9h6.5M8 12h5" /></svg></span>
          <h2>Nenhum curso liberado</h2>
          <p>Você ainda não tem cursos disponíveis. Confira o catálogo para desbloquear.</p>
          <a className="bcm-cta" href="#/catalogo">Abrir catálogo</a>
        </div>
      </div>
    );
  }

  // ---------------- PLAYER ----------------
  if (view === "player" && selectedLesson && currentModule) {
    const lesson = selectedLesson.lesson;
    const isWatched = watchedLessonsSet.has(lesson.id);
    const modWatched = moduleLessons.filter((l) => watchedLessonsSet.has(l.id)).length;
    return (
      <div className="bc-page bc-course">
        <div className="bc-player-head">
          <button type="button" className="bc-back" onClick={() => setView("overview")} aria-label="Voltar aos módulos">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m14 6-6 6 6 6" /></svg>
          </button>
          <div>
            <span className="bc-eyebrow"><i />{currentCourse.title}</span>
            <h1>{currentModule.title}</h1>
          </div>
        </div>

        <div className="bc-player-grid">
          <div className="bc-player-main">
            <div className="bc-video">
              {lesson.video_url ? (
                <iframe
                  src={getEmbedVideoUrl(lesson.video_url)}
                  title={lesson.title}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="bc-video-empty">Vídeo em breve</div>
              )}
            </div>

            <div className="bc-lesson-info">
              <span className="bc-lesson-num">Aula {lessonIdxInModule + 1} de {moduleLessons.length}</span>
              <h2 className="bc-lesson-title">{lesson.title}</h2>

              <div className="bc-lesson-actions">
                <button type="button" className="bc-btn-ghost" disabled={!previousLesson}
                  onClick={() => previousLesson && handleSelectLesson(previousLesson.courseId, previousLesson.courseTitle, previousLesson.lesson.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m14 6-6 6 6 6" /></svg> Anterior
                </button>
                <button type="button" className="bc-btn-primary" disabled={!nextLesson}
                  onClick={() => nextLesson && handleSelectLesson(nextLesson.courseId, nextLesson.courseTitle, nextLesson.lesson.id)}>
                  Próxima aula <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m10 6 6 6-6 6" /></svg>
                </button>
                <button type="button" className={`bc-btn-done ${isWatched ? "is-done" : ""}`}
                  onClick={() => handleSelectLesson(selectedLesson.courseId, selectedLesson.courseTitle, lesson.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m5 12 5 5 9-11" /></svg>
                  {isWatched ? "Aula concluída" : "Marcar como concluído"}
                </button>
              </div>

              {lesson.description ? (
                <div className="bc-lesson-desc">
                  <h3>Sobre esta aula</h3>
                  <p>{lesson.description}</p>
                </div>
              ) : null}

              <div className="bc-materials">
                <h3>Materiais da aula</h3>
                {lesson.materials?.length ? (
                  <div className="bc-materials-list">
                    {lesson.materials.map((m, i) => {
                      const kind = materialKind(m.url);
                      return (
                        <a key={`${lesson.id}-${i}`} className="bc-material" href={m.url} target="_blank" rel="noreferrer"
                          download={kind.download ? "" : undefined}>
                          <span className="bc-material-ic">{kind.download ? <FileIcon /> : <LinkIcon />}</span>
                          <span className="bc-material-txt">
                            <strong>{m.title}</strong>
                            <small>{kind.label}</small>
                          </span>
                          <span className="bc-material-cta">{kind.download ? "Baixar" : "Abrir"}</span>
                        </a>
                      );
                    })}
                  </div>
                ) : (
                  <p className="bc-materials-empty">Nenhum material adicional nesta aula.</p>
                )}
              </div>
            </div>
          </div>

          <aside className="bc-player-side">
            <div className="bc-side-panel-head">
              <strong>Aulas do módulo</strong>
              <span>{modWatched}/{moduleLessons.length} concluídas</span>
            </div>
            <div className="bc-lesson-track">
              {moduleLessons.map((l, i) => {
                const active = l.id === lesson.id;
                const done = watchedLessonsSet.has(l.id);
                return (
                  <button key={l.id} type="button" className={`bc-track-item ${active ? "is-active" : ""}`}
                    onClick={() => handleSelectLesson(selectedLesson.courseId, selectedLesson.courseTitle, l.id)}>
                    <span className="bc-track-thumb">
                      {l.thumbnail_url ? <img src={l.thumbnail_url} alt="" loading="lazy" /> : <span>{i + 1}</span>}
                    </span>
                    <span className="bc-track-copy"><strong>{l.title}</strong></span>
                    <span className={`bc-track-state ${done ? "done" : ""}`}>
                      {done ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="m5 12 5 5 9-11" /></svg> : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>
        </div>
      </div>
    );
  }

  // ---------------- OVERVIEW ----------------
  return (
    <div className="bc-page bc-course">
      <div className="bc-course-banner" style={currentCourse.banner ? { backgroundImage: `url("${currentCourse.banner}")` } : undefined}>
        <div className="bc-course-banner-shade" />
        <div className="bc-course-banner-in">
          <span className="bc-eyebrow"><i />Curso</span>
          <h1>{currentCourse.title}</h1>
          <div className="bc-course-meta">
            <span><b>{modules.length}</b> módulos</span>
            <span><b>{allLessons.length}</b> aulas</span>
            <span className="bc-course-meta-pct"><b>{coursePct}%</b> concluído</span>
          </div>
          <div className="bc-course-cta-row">
            {allLessons.length ? (
              <button type="button" className="bc-btn-primary bc-course-cta" onClick={continueCourse}>
                {watchedCount ? "Continuar de onde parou" : "Começar curso"}
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7L8 5Z" /></svg>
              </button>
            ) : isExternalLive ? (
              <a className="bc-btn-primary bc-course-cta" href={externalUrl} target="_blank" rel="noreferrer">
                Assistir ao vivo
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7L8 5Z" /></svg>
              </a>
            ) : (
              <button type="button" className="bc-btn-primary bc-course-cta" disabled>
                Em breve
              </button>
            )}
            {overallProgress >= 100 ? (
              <button type="button" className="bc-course-cert" onClick={onOpenCertificates}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="9" r="5" /><path d="M8.5 13.5 7 21l5-2 5 2-1.5-7.5" /></svg>
                Certificado
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <h2 className="bc-course-h2">{!modules.length && isExternalLive ? "Acompanhe ao vivo" : "Módulos do treinamento"}</h2>
      {modules.length ? (
        <div className="bc-module-grid">
          {modules.map((mod, i) => {
            const mw = mod.lessons.filter((l) => watchedLessonsSet.has(l.id)).length;
            const pct = mod.lessons.length ? Math.round((mw / mod.lessons.length) * 100) : 0;
            return (
              <button key={mod.id} type="button" className="bc-module-card" onClick={() => openModule(currentCourse, mod)} disabled={!mod.lessons.length}>
                <div className="bc-module-cover" style={mod.cover_url ? { backgroundImage: `url("${mod.cover_url}")` } : undefined}>
                  <span className="bc-module-badge">Módulo {i + 1}</span>
                  {pct === 100 && mod.lessons.length ? <span className="bc-module-done"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="m5 12 5 5 9-11" /></svg></span> : null}
                </div>
                <div className="bc-module-body">
                  <strong>{mod.title}</strong>
                  <div className="bc-module-prog"><i style={{ width: `${pct}%` }} /></div>
                  <small>{mw}/{mod.lessons.length} aulas · {mod.lessons.length ? `${pct}%` : "em breve"}</small>
                </div>
              </button>
            );
          })}
        </div>
      ) : isExternalLive ? (
        <a className="bc-livecourse" href={externalUrl} target="_blank" rel="noreferrer">
          <span className="bc-livecourse-ic">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7L8 5Z" /></svg>
          </span>
          <span className="bc-livecourse-body">
            <strong>Transmissões ao vivo</strong>
            <small>As operações ao vivo acontecem no canal do YouTube. Toque para assistir às transmissões.</small>
          </span>
          <span className="bc-livecourse-cta">
            Abrir
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 6 6 6-6 6" /></svg>
          </span>
        </a>
      ) : (
        <div className="bc-empty">Os módulos deste curso vão aparecer aqui em breve.</div>
      )}

      {allLessons.length ? (
        <button type="button" className={`bc-cert-card ${overallProgress >= 100 ? "is-ready" : ""}`} onClick={onOpenCertificates}>
          <span className="bc-cert-ic">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="9" r="5" /><path d="M8.5 13.5 7 21l5-2 5 2-1.5-7.5" /></svg>
          </span>
          <div className="bc-cert-info">
            <span className="bc-cert-eyebrow">Certificado de conclusão</span>
            {overallProgress >= 100 ? (
              <>
                <strong>Parabéns! Seu certificado está liberado.</strong>
                <small>Certificado único de conclusão do programa — baixe com o seu nome no painel de certificados.</small>
              </>
            ) : (
              <>
                <strong>Conclua 100% de todo o material para liberar</strong>
                <small>Você está em <b>{overallProgress}%</b> do programa. O certificado é único e libera quando você termina todos os cursos.</small>
                <div className="bc-cert-prog"><i style={{ width: `${overallProgress}%` }} /></div>
              </>
            )}
          </div>
          <span className="bc-cert-lock-cta">
            {overallProgress >= 100 ? "Baixar" : "Ver progresso"}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 6 6 6-6 6" /></svg>
          </span>
        </button>
      ) : null}
    </div>
  );
};

export default CoursePage;
